import { useState } from 'react';
import { Node, mergeAttributes } from '@tiptap/core';
import type { JSONContent } from '@tiptap/core';
import type { NodeViewProps } from '@tiptap/react';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import {
  addColumnAfter,
  addColumnBefore,
  addRowAfter,
  addRowBefore,
  deleteColumn,
  deleteRow,
  goToNextCell,
  tableEditing,
} from '@tiptap/pm/tables';
import type { Command } from '@tiptap/pm/state';

import { t } from '../i18n';
import { ActionMenu, ActionMenuItem } from './ActionMenu';

const INITIAL_ROWS = 3;
const INITIAL_COLUMNS = 2;
const MAX_ROWS = 100;
const MAX_COLUMNS = 24;

export function createSimpleTable(): JSONContent {
  return {
    type: 'table',
    content: Array.from({ length: INITIAL_ROWS }, (_, row) => ({
      type: 'tableRow',
      content: Array.from({ length: INITIAL_COLUMNS }, (_, column) => ({
        type: row === 0 ? 'tableHeader' : 'tableCell',
        content: [
          {
            type: 'paragraph',
            ...(row === 0
              ? {
                  content: [
                    { type: 'text', text: t('table.column', { number: String(column + 1) }) },
                  ],
                }
              : {}),
          },
        ],
      })),
    })),
  };
}

type TableAxis = 'row' | 'column';

function TableOptions({
  axis,
  index,
  count,
  run,
}: {
  axis: TableAxis;
  index: number;
  count: number;
  run: (axis: TableAxis, index: number, command: Command) => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const row = axis === 'row';
  const label = t(row ? 'table.rowOptions' : 'table.columnOptions', { number: String(index + 1) });
  return (
    <ActionMenu
      open={open}
      onOpenChange={setOpen}
      label={label}
      align="start"
      rootClassName={`simple-table__grip simple-table__grip--${axis}`}
      trigger={
        <button type="button" className="nb simple-table__grip-button" aria-label={label}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            {(row
              ? [
                  [9, 6],
                  [15, 6],
                  [9, 12],
                  [15, 12],
                  [9, 18],
                  [15, 18],
                ]
              : [
                  [6, 9],
                  [12, 9],
                  [18, 9],
                  [6, 15],
                  [12, 15],
                  [18, 15],
                ]
            ).map(([cx, cy], dot) => (
              <circle key={dot} cx={cx} cy={cy} r="1.8" />
            ))}
          </svg>
        </button>
      }
    >
      <ActionMenuItem
        disabled={count >= (row ? MAX_ROWS : MAX_COLUMNS)}
        onSelect={() => run(axis, index, row ? addRowBefore : addColumnBefore)}
      >
        {t(row ? 'table.insertAbove' : 'table.insertLeft')}
      </ActionMenuItem>
      <ActionMenuItem
        disabled={count >= (row ? MAX_ROWS : MAX_COLUMNS)}
        onSelect={() => run(axis, index, row ? addRowAfter : addColumnAfter)}
      >
        {t(row ? 'table.insertBelow' : 'table.insertRight')}
      </ActionMenuItem>
      <ActionMenuItem
        danger
        disabled={count <= 1}
        onSelect={() => run(axis, index, row ? deleteRow : deleteColumn)}
      >
        {t(row ? 'table.deleteRow' : 'table.deleteColumn')}
      </ActionMenuItem>
    </ActionMenu>
  );
}

function SimpleTableView({ node, editor, getPos }: NodeViewProps): React.ReactElement {
  const rows = node.childCount;
  const columns = node.firstChild?.childCount ?? 0;
  const editable = editor.isEditable;
  const grid = { gridTemplateColumns: `repeat(${columns}, minmax(120px, 1fr))` };

  function run(axis: TableAxis, index: number, command: Command): void {
    if (!editor.isEditable) return;
    const pos = getPos();
    if (typeof pos !== 'number') return;
    let cellPos = pos + 1;
    for (let row = 0; row < (axis === 'row' ? index : 0); row += 1)
      cellPos += node.child(row).nodeSize;
    const rowNode = node.child(axis === 'row' ? index : 0);
    cellPos += 1;
    for (let column = 0; column < (axis === 'column' ? index : 0); column += 1)
      cellPos += rowNode.child(column).nodeSize;
    editor
      .chain()
      .focus()
      .setTextSelection(cellPos + 2)
      .command(({ state, dispatch }) => command(state, dispatch))
      .command(({ tr }) => {
        const table = tr.doc.nodeAt(pos);
        table?.forEach((row, rowOffset, rowIndex) =>
          row.forEach((cell, cellOffset) => {
            const type = tr.doc.type.schema.nodes[rowIndex === 0 ? 'tableHeader' : 'tableCell'];
            if (type && cell.type !== type)
              tr.setNodeMarkup(pos + 2 + rowOffset + cellOffset, type, cell.attrs);
          }),
        );
        return true;
      })
      .run();
  }

  return (
    <NodeViewWrapper className="simple-table" data-simple-table="">
      <div className="simple-table__scroll">
        <div className="simple-table__columns" style={grid} contentEditable={false}>
          {editable &&
            Array.from({ length: columns }, (_, index) => (
              <TableOptions key={index} axis="column" index={index} count={columns} run={run} />
            ))}
        </div>
        <div className="simple-table__frame" style={{ minWidth: columns * 120 }}>
          <NodeViewContent<'table'> as="table" aria-label={t('editor.table')} />
          {editable && (
            <>
              <div className="simple-table__rows" contentEditable={false}>
                {Array.from({ length: rows }, (_, index) => (
                  <TableOptions key={index} axis="row" index={index} count={rows} run={run} />
                ))}
              </div>
              <button
                type="button"
                contentEditable={false}
                className="nb simple-table__add simple-table__add--row"
                aria-label={t('table.addRow')}
                disabled={rows >= MAX_ROWS}
                onClick={() => run('row', rows - 1, addRowAfter)}
              >
                +
              </button>
              <button
                type="button"
                contentEditable={false}
                className="nb simple-table__add simple-table__add--column"
                aria-label={t('table.addColumn')}
                disabled={columns >= MAX_COLUMNS}
                onClick={() => run('column', columns - 1, addColumnAfter)}
              >
                +
              </button>
            </>
          )}
        </div>
      </div>
    </NodeViewWrapper>
  );
}

export const SimpleTable = Node.create({
  name: 'table',
  group: 'block',
  content: 'tableRow+',
  isolating: true,
  parseHTML: () => [{ tag: 'table' }],
  renderHTML: () => ['table', ['tbody', 0]],
  addNodeView: () => ReactNodeViewRenderer(SimpleTableView, { contentDOMElementTag: 'tbody' }),
  extendNodeSchema(extension) {
    const roles: Record<string, string> = {
      table: 'table',
      tableRow: 'row',
      tableCell: 'cell',
      tableHeader: 'header_cell',
    };
    return { tableRole: roles[extension.name] };
  },
  addProseMirrorPlugins: () => [tableEditing()],
  addKeyboardShortcuts() {
    return {
      Tab: () =>
        this.editor.commands.command(({ state, dispatch }) => goToNextCell(1)(state, dispatch)),
      'Shift-Tab': () =>
        this.editor.commands.command(({ state, dispatch }) => goToNextCell(-1)(state, dispatch)),
    };
  },
});

export const SimpleTableRow = Node.create({
  name: 'tableRow',
  content: '(tableCell | tableHeader)+',
  parseHTML: () => [{ tag: 'tr' }],
  renderHTML: () => ['tr', 0],
});

function cell(name: string, tag: string) {
  return Node.create({
    name,
    content: 'paragraph',
    isolating: true,
    addAttributes: () => ({
      colspan: { default: 1 },
      rowspan: { default: 1 },
      colwidth: { default: null },
    }),
    parseHTML: () => [{ tag }],
    renderHTML: ({ HTMLAttributes }) => [tag, mergeAttributes(HTMLAttributes), 0],
  });
}

export const SimpleTableCell = cell('tableCell', 'td');
export const SimpleTableHeader = cell('tableHeader', 'th');
export const SIMPLE_TABLE_EXTENSIONS = [
  SimpleTable,
  SimpleTableRow,
  SimpleTableCell,
  SimpleTableHeader,
];
